import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Card,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Pagination,
    CircularProgress,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Grid,
    Chip,
} from "@mui/material";
import {
    Search,
    AddCircleOutline,
    Refresh,
    Close,
    Inventory2Outlined,
    EditOutlined,
    DeleteOutline,
    WarningAmberRounded,
    FilterAltOff,
} from "@mui/icons-material";
import { WB_SX } from "../../../ui/ux/uiTokens";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import useNotification from "../../common/Notification";
import { isCrmAdmin } from "../common/crmRoles";

/** Backend stores service duration as integer days (1 = 1 day … max 3 years). */
const DURATION_MIN_DAYS = 1;
const DURATION_MAX_DAYS = 3 * 365; // 1095 — 3 years in days

const DURATION_PRESETS = [
    { label: "1 day", days: 1 },
    { label: "1 week", days: 7 },
    { label: "1 month (30 days)", days: 30 },
    { label: "3 months", days: 90 },
    { label: "6 months", days: 180 },
    { label: "1 year", days: 365 },
    { label: "2 years", days: 730 },
    { label: "3 years", days: DURATION_MAX_DAYS },
];

const coerceDurationDays = (raw) => {
    if (raw == null || raw === "") return null;
    if (typeof raw === "number" && Number.isFinite(raw)) return Math.round(raw);
    const n = Number(String(raw).trim());
    if (!Number.isNaN(n) && Number.isFinite(n)) return Math.round(n);
    return null;
};

/** Parse API value into form string; legacy JSON window → day count */
const parseDurationFromApi = (raw) => {
    const n = coerceDurationDays(raw);
    if (n != null) {
        const clamped = Math.min(DURATION_MAX_DAYS, Math.max(DURATION_MIN_DAYS, n));
        return { days: String(clamped), legacy: "" };
    }
    const s = String(raw ?? "").trim();
    if (!s) return { days: "", legacy: "" };
    try {
        const o = JSON.parse(s);
        if (o && typeof o.start === "string" && typeof o.end === "string") {
            const diffMs = new Date(o.end) - new Date(o.start);
            if (!Number.isNaN(diffMs)) {
                const days = Math.max(DURATION_MIN_DAYS, Math.round(diffMs / 86400000));
                const clamped = Math.min(DURATION_MAX_DAYS, days);
                return { days: String(clamped), legacy: "" };
            }
        }
    } catch (_) {
        /* not JSON */
    }
    return { days: "", legacy: s };
};

const formatMoney = (n) => {
    if (n === null || n === undefined || n === "") return "—";
    const num = Number(n);
    if (Number.isNaN(num)) return String(n);
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
    }).format(num);
};

const EMPTY_FORM = {
    name: "",
    type: "product",
    price: "",
    description: "",
    /** Service duration in days (integer sent to API) — only when type === "service" */
    durationDays: "",
    /** Unparseable legacy value from API (optional hint) */
    durationLegacy: "",
    status: "active",
};

/** Build POST body — only fields collected in the UI (see API for optional server-side fields). */
const buildCreatePayload = (form) => {
    const price = Number(form.price);
    if (!form.name?.trim()) throw new Error("Name is required");
    if (!form.type) throw new Error("Type is required");
    if (Number.isNaN(price) || price < 0) throw new Error("Valid price is required");
    const payload = {
        name: form.name.trim(),
        type: form.type,
        price,
    };
    if (form.description?.trim()) payload.description = form.description.trim();
    if (form.type === "service") {
        const d = parseInt(form.durationDays, 10);
        if (!Number.isNaN(d) && d >= DURATION_MIN_DAYS && d <= DURATION_MAX_DAYS) {
            payload.duration = d;
        }
    }
    if (form.status) payload.status = form.status;
    return payload;
};

/** Build PUT body — only fields from the UI; category, tax, sku, discount, stock are not sent (unchanged on server). */
const buildUpdatePayload = (form) => {
    const price = form.price === "" ? undefined : Number(form.price);
    if (price !== undefined && (Number.isNaN(price) || price < 0)) throw new Error("Valid price is required");
    const payload = {
        name: form.name?.trim(),
        type: form.type,
        description: form.description?.trim() ?? "",
        status: form.status,
    };
    if (price !== undefined) payload.price = price;
    if (form.type === "service") {
        const d = parseInt(form.durationDays, 10);
        if (!Number.isNaN(d) && d >= DURATION_MIN_DAYS && d <= DURATION_MAX_DAYS) {
            payload.duration = d;
        }
    } else {
        payload.duration = null;
    }
    return payload;
};

const rowToForm = (row) => {
    const d = parseDurationFromApi(row.duration);
    return {
        name: row.name ?? "",
        type: row.type ?? "product",
        price: row.price != null ? String(row.price) : "",
        description: row.description ?? "",
        durationDays: d.days,
        durationLegacy: d.legacy,
        status: row.status ?? "active",
    };
};

const ProductFormDialog = ({ open, mode, initial, onClose, onSaved }) => {
    const notify = useNotification();
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (mode === "edit" && initial) setForm(rowToForm(initial));
        else setForm({ ...EMPTY_FORM });
    }, [open, mode, initial]);

    const setField = (k) => (e) => {
        const v = e.target.value;
        setForm((f) => ({ ...f, [k]: v }));
    };

    const handleSubmit = async () => {
        setSaving(true);
        try {
            if (mode === "create") {
                const payload = buildCreatePayload(form);
                await axiosInstance.post(`${base_url}/crm/products-services`, payload);
                notify("Product / service created", "success");
            } else {
                const id = initial?.id;
                if (!id) return;
                const payload = buildUpdatePayload(form);
                await axiosInstance.put(`${base_url}/crm/products-services/${id}`, payload);
                notify("Saved", "success");
            }
            onSaved();
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.message || err.message || "Save failed";
            notify(msg, "error");
        } finally {
            setSaving(false);
        }
    };

    const title = mode === "create" ? "Add product / service" : "Edit product / service";

    return (
        <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, fontSize: "1rem", pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: "rgba(17,24,39,0.06)",
                            display: "grid",
                            placeItems: "center",
                        }}
                    >
                        <Inventory2Outlined sx={{ fontSize: 18, color: "text.primary" }} />
                    </Box>
                    {title}
                </Box>
                <IconButton size="small" onClick={onClose} disabled={saving}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2.5 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            required
                            label="Name"
                            value={form.name}
                            onChange={setField("name")}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Type</InputLabel>
                            <Select
                                label="Type"
                                value={form.type}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setForm((f) => ({
                                        ...f,
                                        type: v,
                                        ...(v === "product" ? { durationDays: "", durationLegacy: "" } : {}),
                                    }));
                                }}
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value="product">Product</MenuItem>
                                <MenuItem value="service">Service</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required={mode === "create"}
                            label="Price"
                            type="number"
                            inputProps={{ min: 0, step: "any" }}
                            value={form.price}
                            onChange={setField("price")}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            value={form.description}
                            onChange={setField("description")}
                            size="small"
                            multiline
                            minRows={2}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>
                    {form.type === "service" && (
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 600 }}>
                                Service duration (stored as days, integer)
                            </Typography>
                            {form.durationLegacy ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                                    Could not map previous value: {form.durationLegacy}
                                </Typography>
                            ) : null}
                            <Grid container spacing={2} alignItems="flex-start">
                                <Grid item xs={12}>
                                    <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: "text.primary" }}>
                                        Quick preset
                                    </Typography>
                                    <FormControl fullWidth size="small" variant="outlined">
                                        <Select
                                            displayEmpty
                                            value={(() => {
                                                const n = parseInt(form.durationDays, 10);
                                                if (Number.isNaN(n)) return "";
                                                const hit = DURATION_PRESETS.some((p) => p.days === n);
                                                return hit ? String(n) : "";
                                            })()}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                if (v === "") {
                                                    setForm((f) => ({ ...f, durationDays: "" }));
                                                    return;
                                                }
                                                setForm((f) => ({ ...f, durationDays: String(v) }));
                                            }}
                                            renderValue={(selected) => {
                                                if (selected !== "" && selected != null) {
                                                    const p = DURATION_PRESETS.find((x) => String(x.days) === String(selected));
                                                    return p ? p.label : selected;
                                                }
                                                const n = parseInt(form.durationDays, 10);
                                                if (!Number.isNaN(n) && form.durationDays !== "") {
                                                    return `${n} days (custom)`;
                                                }
                                                return (
                                                    <Typography component="span" variant="body2" color="text.secondary">
                                                        Choose a preset…
                                                    </Typography>
                                                );
                                            }}
                                            sx={{ borderRadius: 2 }}
                                        >
                                            <MenuItem value="">
                                                <em>None — enter days below</em>
                                            </MenuItem>
                                            {DURATION_PRESETS.map((p) => (
                                                <MenuItem key={p.days} value={String(p.days)}>
                                                    {p.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Duration (days)"
                                        type="number"
                                        inputProps={{ min: DURATION_MIN_DAYS, max: DURATION_MAX_DAYS, step: 1 }}
                                        value={form.durationDays}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            if (v === "") {
                                                setForm((f) => ({ ...f, durationDays: "" }));
                                                return;
                                            }
                                            const n = parseInt(v, 10);
                                            if (Number.isNaN(n)) return;
                                            const clamped = Math.min(DURATION_MAX_DAYS, Math.max(DURATION_MIN_DAYS, n));
                                            setForm((f) => ({ ...f, durationDays: String(clamped) }));
                                        }}
                                        size="small"
                                        helperText={`${DURATION_MIN_DAYS}–${DURATION_MAX_DAYS} days (1 day to 3 years)`}
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select label="Status" value={form.status} onChange={setField("status")} sx={{ borderRadius: 2 }}>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button size="small" onClick={onClose} disabled={saving} sx={WB_SX.secondaryButton}>
                    Cancel
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving || (mode === "create" && (!form.name.trim() || form.price === ""))}
                    sx={WB_SX.primaryButton}
                >
                    {saving ? <CircularProgress size={18} color="inherit" /> : mode === "create" ? "Create" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const DeleteDialog = ({ open, target, onClose, onConfirm, deleting }) => (
    <Dialog open={open} onClose={() => !deleting && onClose()} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700, fontSize: "0.95rem" }}>
            <WarningAmberRounded color="warning" />
            Delete item?
        </DialogTitle>
        <DialogContent>
            <Typography variant="body2" color="text.secondary">
                Remove <strong>{target?.name || "this item"}</strong> from the catalog? This cannot be undone.
            </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button size="small" onClick={onClose} disabled={deleting} sx={WB_SX.secondaryButton}>
                Cancel
            </Button>
            <Button
                size="small"
                variant="contained"
                onClick={onConfirm}
                disabled={deleting}
                sx={{ ...WB_SX.primaryButton, backgroundColor: "error.main", "&:hover": { backgroundColor: "error.dark" } }}
            >
                {deleting ? "Deleting…" : "Delete"}
            </Button>
        </DialogActions>
    </Dialog>
);

const CRMProductsServices = () => {
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;
    const admin = isCrmAdmin();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const perPage = 15;
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [loading, setLoading] = useState(false);
    const [dialogMode, setDialogMode] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const searchTimer = useRef(null);

    const fetchList = useCallback(
        async (pg = page, q = search, tf = typeFilter, sf = statusFilter) => {
            setLoading(true);
            try {
                const params = { page: pg, per_page: perPage };
                if (q.trim()) params.search = q.trim();
                if (tf) params.type = tf;
                if (sf) params.status = sf;

                const res = await axiosInstance.get(`${base_url}/crm/products-services`, { params });
                const paginator = res.data?.data ?? {};
                const list = Array.isArray(paginator.data) ? paginator.data : [];
                setRows(list);
                setTotal(paginator.total ?? list.length);
            } catch (err) {
                notifyRef.current(err?.response?.data?.message || "Failed to load catalog", "error");
            } finally {
                setLoading(false);
            }
        },
        [page, search, typeFilter, statusFilter, perPage]
    );

    useEffect(() => {
        fetchList(page, search, typeFilter, statusFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, typeFilter, statusFilter]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setPage(1);
            fetchList(1, val, typeFilter, statusFilter);
        }, 400);
    };

    const filtersActive = Boolean(search.trim() || typeFilter || statusFilter);

    const handleResetFilters = () => {
        clearTimeout(searchTimer.current);
        setSearch("");
        setTypeFilter("");
        setStatusFilter("");
        setPage(1);
        fetchList(1, "", "", "");
    };

    const totalPages = Math.max(1, Math.ceil(total / perPage));

    const confirmDelete = async () => {
        if (!deleteTarget?.id) return;
        setDeleting(true);
        try {
            await axiosInstance.delete(`${base_url}/crm/products-services/${deleteTarget.id}`);
            notifyRef.current("Deleted", "success");
            setDeleteTarget(null);
            fetchList(page, search, typeFilter, statusFilter);
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Delete failed", "error");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={WB_SX.page}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3, flexWrap: "wrap", gap: 2 }}>
                <Box>
                    <Typography variant="h5" sx={WB_SX.pageTitle}>
                        Products / Services
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Catalog for deals and quotes · {total} item{total !== 1 ? "s" : ""}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                    {admin && (
                        <Button
                            variant="contained"
                            startIcon={<AddCircleOutline fontSize="small" />}
                            onClick={() => {
                                setEditTarget(null);
                                setDialogMode("create");
                            }}
                            sx={WB_SX.primaryButton}
                        >
                            Add item
                        </Button>
                    )}
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => fetchList(page, search, typeFilter, statusFilter)} sx={WB_SX.secondaryButton}>
                            <Refresh fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5, alignItems: "center" }}>
                <TextField
                    size="small"
                    placeholder="Search name…"
                    value={search}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ fontSize: 18, color: "text.disabled" }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 260, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.85rem" } }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                        label="Type"
                        value={typeFilter}
                        onChange={(e) => {
                            setTypeFilter(e.target.value);
                            setPage(1);
                        }}
                        sx={{ borderRadius: 2 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="product">Product</MenuItem>
                        <MenuItem value="service">Service</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPage(1);
                        }}
                        sx={{ borderRadius: 2 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                </FormControl>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FilterAltOff fontSize="small" />}
                    onClick={handleResetFilters}
                    disabled={!filtersActive}
                    sx={{ ...WB_SX.secondaryButton, textTransform: "none", fontWeight: 600 }}
                >
                    Reset filters
                </Button>
            </Box>

            <Card sx={WB_SX.card}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {["Name", "Type", "Price", "Status", "Created by", "Actions"].map((h) => (
                                    <TableCell key={h} sx={WB_SX.tableHeadCell}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={32} />
                                    </TableCell>
                                </TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: "text.secondary" }}>
                                        No products or services yet.
                                        {admin ? " Use Add item to create your catalog." : ""}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((row) => {
                                    const svcDays = row.type === "service" ? coerceDurationDays(row.duration) : null;
                                    return (
                                    <TableRow key={row.id} hover sx={WB_SX.tableRow}>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {row.name}
                                            </Typography>
                                            {row.description ? (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 280 }} noWrap>
                                                    {row.description}
                                                </Typography>
                                            ) : null}
                                            {svcDays != null ? (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                                                    Duration: {svcDays} day{svcDays === 1 ? "" : "s"}
                                                </Typography>
                                            ) : null}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={row.type === "service" ? "Service" : "Product"}
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: "0.7rem",
                                                    backgroundColor: row.type === "service" ? "#eff6ff" : "#f0fdf4",
                                                    color: row.type === "service" ? "#1d4ed8" : "#166534",
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{formatMoney(row.price)}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={row.status === "inactive" ? "Inactive" : "Active"}
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: "0.7rem",
                                                    backgroundColor: row.status === "inactive" ? "#fef2f2" : "#ecfdf5",
                                                    color: row.status === "inactive" ? "#991b1b" : "#047857",
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {row.createdBy?.name || row.created_by_user?.name || "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {admin ? (
                                                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setEditTarget(row);
                                                                setDialogMode("edit");
                                                            }}
                                                        >
                                                            <EditOutlined fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}>
                                                            <DeleteOutline fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            ) : (
                                                <Typography variant="caption" color="text.disabled">
                                                    —
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, p) => setPage(p)}
                        color="primary"
                        shape="rounded"
                        siblingCount={1}
                    />
                </Box>
            )}

            <ProductFormDialog
                open={dialogMode === "create" || dialogMode === "edit"}
                mode={dialogMode === "edit" ? "edit" : "create"}
                initial={editTarget}
                onClose={() => {
                    setDialogMode(null);
                    setEditTarget(null);
                }}
                onSaved={() => fetchList(page, search, typeFilter, statusFilter)}
            />

            <DeleteDialog
                open={Boolean(deleteTarget)}
                target={deleteTarget}
                onClose={() => !deleting && setDeleteTarget(null)}
                onConfirm={confirmDelete}
                deleting={deleting}
            />
        </Box>
    );
};

export default CRMProductsServices;
